import { useDispatch } from 'wrapped-redux';
import * as actions from '@actions';
import { useMutation } from '@tanstack/react-query';

export type RescheduleAppointmentParams = {
  user_guid: string;
  appt_id: number;
  date: Date;
  clin_user_guid: string;
  call_type_code: string;
  reason?: string;
};

const ERROR_MAP = {
  403: { message: 'You are not currently authorised to make this booking.' },
  409: {
    message: 'This appointment is no longer available. Please choose another time.'
  },
  410: { message: 'This case appointment booking is not allowed. This may be because the case is closed.' },
  412: {
    message: 'Less than 10 minutes before the start of the appointment. Please choose another time.'
  },
  423: { message: 'Your appointment cannot be rescheduled. This may be because you have already attended it.' },
  425: {
    message: 'You are not able to book an appointment for a date/time in the past. Please choose another time.'
  },
  451: { message: 'You are not currently eligible to book an appointment for this case.' },
  500: { message: 'An error occurred. Please try again later.' }
};

const getError = async (response: any) => {
  let error;
  try {
    const errorFromResult =
      response?.error?.data || response?.error || (await response?.payload?.json()) || (await response?.error?.json());
    error = {
      code: errorFromResult.code || errorFromResult.status,
      message: errorFromResult.message
    };
  } catch (e) {
    console.error('Error parsing error code', e);
    error = { code: 500, message: 'An error occurred. Please try again later.' };
  }
  return (
    // @ts-expect-error
    error?.message || ERROR_MAP[error?.code]?.message || 'An error occurred. Please try again later.'
  );
};

const useRescheduleAppointmentMutation = () => {
  const dispatch = useDispatch();

  const mutation = useMutation({
    mutationFn: async (payload: RescheduleAppointmentParams) => {
      const response = await dispatch(
        actions.rescheduleConfirm({
          user_guid: payload.user_guid,
          appt_id: payload.appt_id,
          date: new Date(payload.date),
          clin_user_guid: payload.clin_user_guid,
          call_type_code: payload.call_type_code,
          reason: payload.reason
        })
      );

      if (response?.type === 'RESCHEDULE_CONFIRM_FAIL') {
        throw new Error(await getError(response));
      }
    }
  });

  return {
    rescheduleAppointment: (params: RescheduleAppointmentParams) => mutation.mutateAsync(params),
    ...mutation
  };
};

export default useRescheduleAppointmentMutation;
